import React from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import { cardImageUrl } from '../services/helpers';
import { EnergySymbol } from '../Energy';
import { energySymbols } from '../Energy/energySymbols';
import { Art, CARD_ASPECT, Count, Energies, Face, Gem, Gems, StatusBadges, Trainers } from './styled';
import { gemLayout } from './gemLayout';
import { seedOf } from '../random';
import { AttachedTrainer, ConditionMarker } from './Markers';
import { conditionDisplay } from './conditionDisplay';
import useLeavingGems from './useLeavingGems';

/**
 * How a card turned for a Special Condition sits. Sideways it's shorter than it is tall, so turning it about its middle
 * would leave its bottom edge floating above the line it stood on; it moves down by the difference, so it rests on
 * the line as a real card turned on the mat would.
 */
const SIDEWAYS_DROP = (1 / CARD_ASPECT - 1) / 2;
const turned = (turn) => (Math.abs(turn) === 90
  ? `translateY(calc(var(--card-w, 64px) * ${SIDEWAYS_DROP})) rotate(${turn}deg)`
  : `rotate(${turn}deg)`);

/**
 * A card as it shows on the board or in a hand. With no card (or a face-down one) it shows the card back.
 */
const CardFace = ({
  card, label, damage, damageSeed, conditions, protection, hindrance, hinderedBy, trainers, conversions, energyAttachments,
  count, targetable, dim, selected, full,
}) => {
  // One glass gem per 10 damage, scattered over the artwork (gemLayout); the same Pokémon lays them out the same way.
  const seed = damageSeed ?? seedOf(`card-${card ? card.get('id') : ''}`);
  const { size: gemSize, gems } = gemLayout(damage > 0 ? damage : 0, seed);
  const leavingGems = useLeavingGems(gems, seed);
  const symbols = energySymbols(energyAttachments);
  // Asleep, Paralyzed and Confused turn the card; poison and burn put a marker on it; anything else is in words.
  const shown = conditionDisplay(conditions ? conditions.toArray() : []);
  const hasConditions = shown.words.length > 0;
  const hasTrainers = trainers && trainers.size > 0;
  const hasConversions = conversions && conversions.size > 0;

  return (
    <Face
      // A turned card turns everything on it, the way real attached cards turn with the Pokémon; the badges you read
      // (damage, markers, words) turn back upright (--upright).
      style={shown.turn ? { transform: turned(shown.turn), '--upright': `rotate(${-shown.turn}deg)` } : undefined}
      data-turn={shown.turn || undefined}
      $targetable={targetable}
      data-targetable={targetable ? 'true' : undefined}
      $dim={dim}
      $selected={selected}
      role="img"
      aria-label={[
        label || (card && card.get('name')) || 'Face-down card',
        ...(damage > 0 ? [`${damage} damage`] : []),
        ...(conditions ? conditions.toArray() : []),
      ].join(', ')}
    >
      <Art style={{ backgroundImage: `url(${cardImageUrl(card, { full })})` }} />
      {gems.length + leavingGems.length > 0 && <Gems>
        {gems.map((gem, i) => (
          <Gem key={i} $color={gem.color} $size={gemSize} style={{ left: `${gem.x * 100}%`, top: `${gem.y * 100}%` }} />
        ))}
        {leavingGems.map((gem, i) => (
          <Gem
            key={gems.length + i}
            $leaving
            $color={gem.color}
            $size={gemSize}
            style={{ left: `${gem.x * 100}%`, top: `${gem.y * 100}%` }}
          />
        ))}
      </Gems>}
      {(hasConditions || protection || hinderedBy || hasConversions) && <StatusBadges>
        {hasConditions && shown.words.map(name => <li key={name}>{name}</li>)}
        {protection && <li data-kind="protection" title={protection} aria-label={protection}>Protected</li>}
        {hinderedBy && <li data-kind="hindrance" title={hindrance} aria-label={`${hinderedBy}: ${hindrance}`}>{hinderedBy}</li>}
        {hasConversions && conversions.map(text => <li key={text} data-kind="conversion">{text}</li>)}
      </StatusBadges>}
      {symbols.size > 0 && <Energies>
        {/* One group per energy card (a Double Colorless shows two), so a card flying in can hide its own. */}
        {energyAttachments.map((attachment, i) => (
          <span key={attachment.get('id') || i} data-energy-attachment style={{ display: 'contents' }}>
            {energySymbols(List([attachment])).map((type, j) => <EnergySymbol key={j} type={type} size="var(--energy-size)" />)}
          </span>
        ))}
      </Energies>}
      {hasTrainers && <Trainers style={{ '--trainers': trainers.size }}>{trainers.map((trainer, i) => <AttachedTrainer key={i} card={trainer} />)}</Trainers>}
      {/* Markers last: set down on top of everything, like a real token on a card. */}
      {shown.markers.map(kind => <ConditionMarker key={kind} kind={kind} />)}
      {count !== undefined && count !== null && <Count>{count}</Count>}
    </Face>
  );
};

CardFace.propTypes = {
  card: PropTypes.instanceOf(Map),
  /** Shown full size (a card read on its own): the whole scan, not the small copy. */
  full: PropTypes.bool,
  /** What a screen reader calls the card, when its name is not enough (a face-down prize). */
  label: PropTypes.string,
  damage: PropTypes.number,
  /** Which Pokémon in play this is (gemSeed), so its damage counters keep their places; the card's own by default. */
  damageSeed: PropTypes.number,
  /** Special conditions by name, like Asleep or Poisoned; only an active Pokémon has them (conditionDisplay). */
  conditions: PropTypes.instanceOf(List),
  /** Protection from attacks this turn, like Withdraw's, as the server describes it ("Protected from damage"). */
  protection: PropTypes.string,
  /** What an opponent's attack like Amnesia stops this turn ("Can't use Ember"), and that attack's name. */
  hindrance: PropTypes.string,
  hinderedBy: PropTypes.string,
  /** Trainers attached to the Pokémon, like PlusPower or Defender, as catalog cards; one icon each, as they stack. */
  trainers: PropTypes.instanceOf(List),
  /** A Weakness or Resistance an attack changed (Conversion), like "Weak to Fighting". */
  conversions: PropTypes.instanceOf(List),
  energyAttachments: PropTypes.instanceOf(List),
  count: PropTypes.number,
  targetable: PropTypes.bool,
  dim: PropTypes.bool,
  selected: PropTypes.bool,
};

export default CardFace;
